/**
 * 고객 홈 Container
 * --
 */
import React from 'react';
import MainPresenter from './MainPresenter';
import {
  useDefaultShopDataStack,
  useUserDataAction,
  useUserDataStack,
} from 'store';
import { useGetBanners } from 'hooks/BannerHooks';
import { useGetPopups } from 'hooks/PopupHooks';
import dayjs from 'dayjs';
import MessageAlert, {
  getCookie,
  STORE_URL,
  flattenTreeData,
  extractApplicationPolicy,
  calculateRentalMonthlyPayment,
} from 'utils';
import { useUserStack } from 'store';
import { useUpsertBulkCart } from 'hooks/CartHooks';
import lodash from 'utils/Lodash';
import { useGetEvents } from 'hooks/EventHooks';
import { useGetMapPromotions, useGetPromotions } from 'hooks/PromotionHooks';

/**----------------------------------------
 * [Component] 쇼핑몰 > 메인: 컨테이너
 * ----------------------------------------
 */
const MainContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { navigate, screenSize, location } = props;

  /* ===== Mutate ===== */
  const { upsertBulkCart } = useUpsertBulkCart();

  /* ===== VARIABLE ===== */
  const TODAY = dayjs();
  const options = {
    gcTime: Infinity,
    // staleTime: Infinity,
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];
      else return data;
    },
    initialData: [],
  };

  /* ===== Store ===== */
  // 메인페이지
  const { products, categories, policies, promotion, shops, groups } =
    useDefaultShopDataStack();
  const { wishlist, cartList } = useUserDataStack();
  const userInfo = useUserStack();
  const userDataAction = useUserDataAction();

  /* ===== Data ===== */
  // 배너
  const { data: Banners } = useGetBanners(
    {
      banner_type: 'PC',
      is_show: 'Y',
      banner_start_date: `${TODAY.startOf('day').unix()}`,
      banner_end_date: `${TODAY.endOf('day').unix()}`,
    },
    options
  );
  // 팝업
  const { data: Popups } = useGetPopups(
    {
      is_show: 'Y',
    },
    options
  );
  // 이벤트
  const { data: Events } = useGetEvents(
    { is_main: 'Y', is_show: 'Y' },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];
        else return data;
      },
      initialData: [],
    }
  );
  // 프로모션
  const { data: Promotions } = useGetPromotions(
    { is_main: 'Y', is_show: 'Y', examination_status: 'APPROVE' },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200) return [];
        else return data;
      },
      initialData: [],
    }
  );
  // 타임세일
  const { data: TimeSale } = useGetMapPromotions(
    {
      is_main: 'Y',
      is_show: 'Y',
      examination_status: 'APPROVE',
      promotion_type: 'TIME_SALE',
      // promotion_start_date: dayjs().startOf('day').unix(),
      // promotion_end_date: dayjs().endOf('day').unix(),
    },
    {
      select: (res) => {
        const { status, data } = res;

        if (status !== 200 || !data.length) return [];

        const sortData = data
          .map((_item) => ({
            ..._item,
            map_product_policy: _item?.map_product_policy?.filter(
              (_sub) => _sub.is_enable === 'Y'
            ),
          }))
          .sort((a, b) => a.order - b.order);

        return sortData;
      },
      initialData: [],
    }
  );

  /* ===== STATE ===== */
  const [isPopup, setIsPopup] = React.useState(false);

  /* ===== FUNCTION ===== */
  /**
   * 렌탈 디폴트 옵션 추출
   * --
   * 재귀적으로 JSON 데이터를 순회하면서,
   * 배열 내 객체에 `default` 프로퍼티가 있는 경우 true인 항목만 남깁니다.
   */
  const extractDefaultSelections = (data) => {
    if (Array.isArray(data)) {
      // 배열 내에서 default: true인 첫 번째 객체 선택
      const defaultItem = data.find(
        (item) => item && typeof item === 'object' && item.default === true
      );

      if (!defaultItem) return null; // default: true가 없으면 null 반환

      // default로 선택된 객체를 재귀적으로 처리 (내부 배열 포함)
      return extractDefaultSelections({ ...defaultItem });
    } else if (data !== null && typeof data === 'object') {
      const result = {};
      for (const key in data) {
        if (!Object.prototype.hasOwnProperty.call(data, key)) continue;
        if (key.endsWith('_description')) continue; // 설명 필드 제거

        // 배열이면 default 값이 있는 객체를 찾아 적용 (내부 배열도 재귀적으로 처리)
        if (Array.isArray(data[key])) {
          const selected = extractDefaultSelections(data[key]);
          if (selected !== null) result[key] = selected; // 선택된 값이 있다면 유지
        } else {
          result[key] = extractDefaultSelections(data[key]); // 재귀 처리
        }
      }
      return result;
    }
    return data;
  };

  /**
   * 장바구니 추가
   * --
   * @param {array} d
   * @returns {boolean}
   */
  const handleCartList = async (d = null) => {
    // 로그인 여부 확인
    if (!userInfo) {
      if (window.confirm('로그인 후 이용가능합니다.\n로그인하시겠습니까?')) {
        return navigate('signin', {
          state: {
            from: `${location.pathname}${
              location.search ? location.search : ''
            }`,
          },
        });
      } else {
        return false;
      }
    }

    if (!d || !d.length) {
      MessageAlert.info('상품을 선택해주세요!');
      return false;
    }

    // 장바구니 담을 데이터 정제
    const cartData = d?.map((item) => ({
      user_id: userInfo.user_id,
      product_id: item?.key,
      cart_num: item?.cart_num,
      shop_information_id: item?.shopKey,
      product_option: item?.product_option ?? null,
      cart_type: item?.type,
    }));

    // 이미 장바구니에 담겨있는지 체크
    const mutateData = cartData?.map((item) => {
      const isSame = cartList?.find((cart) =>
        item?.product_option
          ? lodash.isEqual(item?.product_option, cart?.product_option)
          : true &&
            item?.user_id === cart?.user_id &&
            item?.product_id === cart?.product_id &&
            item?.shop_information_id === cart?.shop_information_id
      );

      if (isSame)
        return {
          ...item,
          cart_num: isSame.cart_num + item.cart_num,
          cart_id: isSame.cart_id,
        };
      else
        return {
          ...item,
        };
    });

    try {
      const { status, data } = await upsertBulkCart(mutateData);

      if (status !== 200) {
        MessageAlert.info('장바구니 추가에 실패하였습니다.');
        return false;
      }

      let removeList = [...data];
      const newCart = cartList.map((cart) => {
        const findData = data.find((item) => item.cart_id === cart.cart_id);

        if (findData) {
          removeList = removeList.filter(
            (sub) => sub.cart_id === findData.cart_id
          );

          return {
            ...cart,
            ...findData,
          };
        } else {
          return {
            ...cart,
          };
        }
      });

      userDataAction.setData('cartList', newCart.concat(removeList));
      MessageAlert.success('장바구니에 추가되었습니다.');
      return true;
    } catch (err) {
      console.error('[MainContainer][handleCartList] Error!! ', err);
      MessageAlert.error('장바구니 추가에 실패하였습니다.');
      return false;
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 배너
   * --
   */
  const BannerList = React.useMemo(() => {
    if (!Banners.length) return [];

    // 최종관리자(JM)이 등록한 배너 추출
    const filterBanner = Banners.filter(
      (banner) =>
        (!banner.shop_information_id || banner.shop_information_id === 1) &&
        banner.banner_start_date <= TODAY.startOf('day').unix() &&
        banner.banner_end_date >= TODAY.endOf('day').unix()
    );

    // 등급 노출 여부에 따른 배너 추출
    const gradesBanner = filterBanner.filter(
      (banner) =>
        !banner.grade_show ||
        (banner.grade_show &&
          banner.grade_show.some(
            (grade) => grade.grade_id === (userInfo?.grade_id ?? 0)
          ))
    );

    const newBanners = gradesBanner.map((banner) => ({
      ...banner,
      url: `${STORE_URL}/${banner.banner_url}`,
      title: banner.banner_name,
      id: banner.banner_id,
    }));

    // 순서별 정렬
    const sortBanner = newBanners.sort((a, b) => a.order - b.order);

    return sortBanner;
    // eslint-disable-next-line
  }, [Banners, userInfo]);

  /**
   * 팝업
   * --
   */
  const PopupList = React.useMemo(() => {
    if (!Popups.length) return [];

    // 모바일 제외 팝업 필터
    const filterPopup = Popups.filter(
      (popup) =>
        popup.popup_type !== 'MOBILE' &&
        popup.popup_start_date <= TODAY.startOf('day').unix() &&
        (popup.popup_end_date ?? Infinity) >= TODAY.endOf('day').unix()
    );

    // 팝업 데이터 정제
    const newPopupList = filterPopup.map((popup) => ({
      ...popup,
    }));

    return newPopupList;
    // eslint-disable-next-line
  }, [Popups]);

  /**
   * 카테고리 플랫 정제
   * --
   */
  const FlattenCategories = React.useMemo(() => {
    return flattenTreeData(categories);
  }, [categories]);

  /**
   * 상품 목록 정제
   * --
   */
  const ProductList = React.useMemo(() => {
    const newProducts = products?.map((product) => {
      const {
        product_file,
        product_name,
        num,
        price,
        product_status,
        product_category_id,
        product_id,
        shop_information_id,
        created_at,
        average_score,
        review_count,
        tag,
        product_option,
        type: delivery_type,
        packaging_method,
        regular_information,
        rental_information,
        is_best,
        is_new,
      } = product;

      // 썸네일 추출
      const thumbnail =
        product_file.find((file) => file.type === 'THUMBNAIL') || null;

      // 태그 추출
      const tagList = tag.map((tag) => ({
        id: tag.tag_id,
        label: tag.tag_name,
      }));

      // 쇼핑몰 추출
      const shopData = shops.find(
        (shop) => shop.shop_information_id === shop_information_id
      );

      // 적용 정책 추출
      const applyPolicy = policies.filter((policy) =>
        policy.map_product_policy.some((map) => map.product_id === product_id)
      );
      // 우선순위에 따른 적용되는 정책 추출
      const applicationPolicy = applyPolicy.length
        ? extractApplicationPolicy(applyPolicy, price, promotion)
        : null;

      // 카테고리 key 추출
      const categoryKey =
        FlattenCategories.find(
          (category) => category.product_category_id === product_category_id
        ) ?? '';

      // 렌탈 default 옵션
      const rentalOptions =
        delivery_type === 'rental'
          ? extractDefaultSelections(rental_information)
          : null;

      return {
        key: product_id,
        category: categoryKey?.key,
        categoryTitle: categoryKey?.product_category_name,
        title: product_name,
        price:
          delivery_type === 'rental'
            ? calculateRentalMonthlyPayment(price, rentalOptions)
            : price,
        isSoldOut: num === 0 || product_status === 'SOLDOUT' ? 'Y' : 'N',
        src: !thumbnail ? null : `${STORE_URL}/${thumbnail.file_url}`,
        tags: tagList,
        brand: shopData ? shopData.shop_information_name : '',
        shopKey: shop_information_id,
        discount: applicationPolicy
          ? applicationPolicy.discount_type === 'R'
            ? applicationPolicy.apply_discount_rate
            : applicationPolicy.discount_price
          : 0,
        discount_type: applicationPolicy
          ? applicationPolicy.discount_type
          : 'W',
        finalPrice: applicationPolicy
          ? delivery_type === 'rental'
            ? calculateRentalMonthlyPayment(
                price - applicationPolicy.discount_price,
                rentalOptions
              )
            : price - applicationPolicy.discount_price
          : delivery_type === 'rental'
          ? calculateRentalMonthlyPayment(price, rentalOptions)
          : price,
        isWish: wishlist?.some(
          (wish) => wish?.product_id === product_id && wish?.type === 'WISH'
        ),
        create: created_at,
        score: average_score,
        comment: review_count,
        options: product_option,
        type: delivery_type,
        policy: applicationPolicy,
        packaging: packaging_method,
        regularCycle:
          delivery_type === 'SUBSCRIPTION'
            ? regular_information?.regular_cycle
            : null,
        rentalInfo: rentalOptions,
        isBest: is_best,
        isNew: is_new,
      };
    });

    return newProducts;

    // eslint-disable-next-line
  }, [
    products,
    // shops,
    policies,
    FlattenCategories,
    wishlist,
    promotion,
  ]);

  /**
   * 그룹상품 목록 정제
   * --
   */
  const GroupProducts = React.useMemo(() => {
    if (!groups.length || !ProductList.length) return [];

    const mainGroups = groups
      ?.filter((_item) => _item?.is_main === 'Y')
      ?.sort((a, b) => a?.order - b?.order);

    const newGroups = mainGroups?.map((_item) => {
      const { map_product_policy, ...rest } = _item;

      return {
        ...rest,
        products: map_product_policy
          ?.map((map) => {
            const findProduct = ProductList?.find(
              (product) => product?.key === map?.product_id
            );

            if (!findProduct) return null;

            return {
              ...map,
              ...findProduct,
            };
          })
          ?.filter((product) => product),
      };
    });

    return newGroups;
  }, [groups, ProductList]);

  /**
   * 팝업 열림 여부
   * --
   */
  React.useEffect(() => {
    const popupCookie = getCookie('jm-popup-visible');

    if (!popupCookie) setIsPopup(true);
    else setIsPopup(false);
  }, []);

  /* ===== RENDER ===== */
  return (
    <>
      <MainPresenter
        userInfo={userInfo}
        banner={BannerList}
        popup={PopupList}
        navigate={navigate}
        isPopup={isPopup}
        products={ProductList}
        groups={GroupProducts}
        categories={categories}
        setIsPopup={setIsPopup}
        screenSize={screenSize}
        onCartList={handleCartList}
        events={Events}
        promotions={Promotions}
        timeSale={TimeSale}
      />
    </>
  );
};

export default MainContainer;
